import { AxiosError } from 'axios';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import { HttpStatusCodeEnum } from '@/api/httpClient';
import { ALLOWED_AI_FILE_EXTENSIONS } from '@/shared/features/ai-quiz-generation/constants';
import { alertDialog } from '@/ui_v3';

export const AcceptAiFileExtensions = ALLOWED_AI_FILE_EXTENSIONS.map(
  d => `.${d}`,
).join(', ');

const EMPTY_DESCRIPTION = 'EMPTY_DESCRIPTION';

export const useBuildAiFileErrorAlert = () => {
  const { t } = useTranslation(['build', 'common']);

  const timeoutDescription = t('build:build.ai.failAiQuizTimeout');

  const DefaultError = {
    title: t('build.ai.failBuildAiQuiz'),
    description: t('build:build.aiError.changeFile'),
  } as { title: string; description: string };

  return (error: AxiosError | Error, errorEventName: string) => {
    let title = DefaultError.title;
    let description = DefaultError.description;

    if (error instanceof AxiosError) {
      console.error('Ai Server Error', error.message, error.response);
      switch (error.response?.status) {
        case undefined:
          break;
        case HttpStatusCodeEnum.UNAUTHORIZED_401:
        case HttpStatusCodeEnum.FORBIDDEN_403:
          title = t('common:play.noAuthor.error');
          description = EMPTY_DESCRIPTION;
          break;
        case HttpStatusCodeEnum.UNSUPPORTED_MEDIA_TYPE_415: {
          description = t('common:common.validateFile.possibleFileExt', {
            fileExt: ALLOWED_AI_FILE_EXTENSIONS.join(', '),
          });
          break;
        }
        case HttpStatusCodeEnum.LOCKED_423: {
          description = t('build:build.aiError.removeFilePassword');
          break;
        }
        case HttpStatusCodeEnum.PAYLOAD_TOO_LARGE_413:
          description = t('build:build.aiError.ocr500p');
          break;
        case HttpStatusCodeEnum.TOO_MANY_REQUESTS_429:
        case HttpStatusCodeEnum.SERVICE_UNAVAILABLE_503: {
          description = timeoutDescription;
          break;
        }
        case HttpStatusCodeEnum.UNPROCESSABLE_ENTITY_422: {
          description = t('build:build.aiError.ocr500p');
          break;
        }
      }
      if (error.code === 'ECONNABORTED') {
        title = DefaultError.title;
        description = timeoutDescription;
      }
    }
    alertDialog.open({
      header: {
        title,
        hasCloseButton: false,
      },
      body: description === EMPTY_DESCRIPTION ? '' : description,
    });
    logger.track(errorEventName, { error_name: error.name });
  };
};

export const AiFileSizeLimitMb = 30;
